// frontend/src/components/simple/SimpleConsumption.jsx

import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import Card from '../common/Card';
import Loading from '../common/Loading';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

const SimpleConsumption = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('week'); // day, week, month, year ✅
  const [history, setHistory] = useState([]);
  const [estimation, setEstimation] = useState(null);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [period]);

  const fetchData = async () => {
    try {
      // Consommation actuelle
      const response = await api.get('/simple-association/consumption');
      setData(response.data.data);
  
      // Historique
      const historyResponse = await api.get(`/simple-association/consumption/history?period=${period}`);
      
      // ✅ Formater les dates correctement
      const formattedHistory = (historyResponse.data.data || []).map(item => ({
        ...item,
        date: formatDate(item.timestamp, period)
      }));
      
      setHistory(formattedHistory);
  
      // Estimation fin de mois
      if (period === 'month') {
        calculateEstimation(response.data.data, formattedHistory);
      }
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setLoading(false);
    }
  };

  // ✅ FONCTION HELPER MISE À JOUR
  const formatDate = (timestamp, period) => {
    const date = new Date(timestamp);
    
    if (period === 'day') {
      // Format: "14h00"
      return `${date.getHours()}h${String(date.getMinutes()).padStart(2, '0')}`;
      
    } else if (period === 'week') {
      // Format: "Lun 5"
      const days = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];
      return `${days[date.getDay()]} ${date.getDate()}`;
      
    } else if (period === 'month') {
      // Format: "5 fév"
      const months = ['jan', 'fév', 'mar', 'avr', 'mai', 'juin', 'juil', 'août', 'sep', 'oct', 'nov', 'déc'];
      return `${date.getDate()} ${months[date.getMonth()]}`;
      
    } else if (period === 'year') {
      // ✅ Format: "fév"
      const months = ['jan', 'fév', 'mar', 'avr', 'mai', 'juin', 'juil', 'août', 'sep', 'oct', 'nov', 'déc'];
      return `${months[date.getMonth()]}`;
    }
    
    return date.toLocaleDateString('fr-FR');
  };

  const calculateEstimation = (currentData, historyData) => {
    if (!currentData) {
      setEstimation(null);
      return;
    }

    const now = new Date();
    const currentDay = now.getDate();
    const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
    const daysRemaining = daysInMonth - currentDay;

    // ✅ Utiliser l'énergie temps réel Tuya EN PRIORITÉ, sinon le billing
    const realtimeEnergy = currentData.costs?.consumption?.kwh || 0;
    const billingKwh = currentData.costs?.consumption?.kwh || 0;
    const currentConsumption = Math.max(realtimeEnergy, billingKwh);

    if (currentConsumption <= 0 || currentDay === 0) {
      setEstimation(null);
      return;
    }

    const avgPerDay = currentConsumption / currentDay;
    const estimatedRemaining = avgPerDay * daysRemaining;
    const estimatedTotal = currentConsumption + estimatedRemaining;

    const pricePerKwh = currentData.costs?.consumption?.pricePerKwh || 81.03;
    const tvaRate = 0.18;
    
    const estimatedBaseCost = estimatedTotal * pricePerKwh;
    const estimatedTVA = estimatedBaseCost * tvaRate;
    const estimatedConsumptionCost = estimatedBaseCost + estimatedTVA;
    
    const primeFixe = currentData.costs?.fixedCharges?.primeFixe || 9955;
    const tvaPrimeFixe = currentData.costs?.fixedCharges?.tvaPrimeFixe || 1792;
    const totalRedevances = currentData.costs?.taxes?.totalRedevances || 5815;
    
    const estimatedCost = estimatedConsumptionCost + primeFixe + tvaPrimeFixe + totalRedevances;

    setEstimation({
      currentConsumption: Math.round(currentConsumption * 100) / 100,
      estimatedRemaining: Math.round(estimatedRemaining * 100) / 100,
      estimatedTotal: Math.round(estimatedTotal * 100) / 100,
      estimatedCost: Math.round(estimatedCost),
      avgPerDay: Math.round(avgPerDay * 1000) / 1000,
      daysRemaining,
    });
  };

  const formatCurrency = (amount) => {
    return Math.round(amount).toLocaleString('fr-FR');
  };

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border-2 border-gray-200 rounded-lg shadow-lg">
          <p className="text-sm font-semibold text-gray-800 mb-1">
            {payload[0].payload.date}
          </p>
          <p className="text-sm text-blue-600">
            <span className="font-semibold">Consommation:</span> {payload[0].value.toFixed(2)} kWh
          </p>
          {payload[1] && (
            <p className="text-sm text-green-600">
              <span className="font-semibold">Coût:</span> {formatCurrency(payload[1].value)} FCFA
            </p>
          )}
        </div>
      );
    }
    return null;
  };

  if (loading) return <Loading />;

  if (!data) {
    return (
      <div className="p-4 sm:p-6">
        <Card>
          <div className="text-center py-8">
            <span className="text-6xl mb-4 block">⚡</span>
            <h3 className="text-xl font-bold text-gray-800 mb-2">
              Aucune donnée disponible
            </h3>
            <p className="text-gray-600">
              Les données de consommation seront bientôt disponibles
            </p>
          </div>
        </Card>
      </div>
    );
  }

  if (data.notApplicable) {
    return (
      <div className="p-4 sm:p-6">
        <Card>
          <div className="text-center py-12">
            <span className="text-6xl mb-4 block">🌡️</span>
            <h3 className="text-xl font-bold text-gray-800 mb-2">
              Pas de mesure de consommation
            </h3>
            <p className="text-gray-600 max-w-md mx-auto">
              "{data.association?.name}" pilote une climatisation par infrarouge —
              ce type d'équipement n'a pas de compteur électrique. Utilisez le
              Dashboard pour la piloter.
            </p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold bg-gradient-to-r from-green-600 to-teal-600 bg-clip-text text-transparent">
                Ma Consommation
              </h1>
              <p className="text-gray-600 mt-1 text-sm sm:text-base">
                {data.association.name}
              </p>
            </div>
            <div className="text-xs sm:text-sm text-gray-500">
              Dernière mise à jour: {new Date(data.lastUpdate).toLocaleTimeString('fr-FR')}
            </div>
          </div>

          {/* ✅ FILTRES DE PÉRIODE MODIFIÉS */}
          <div className="mt-4 sm:mt-6 flex gap-2 overflow-x-auto pb-2">
            <button
              onClick={() => setPeriod('day')}
              className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-colors ${
                period === 'day'
                  ? 'bg-emerald-500 text-white shadow-md'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              Aujourd'hui
            </button>
            
            <button
              onClick={() => setPeriod('week')}
              className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-colors ${
                period === 'week'
                  ? 'bg-emerald-500 text-white shadow-md'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              7 Jours
            </button>
            
            <button
              onClick={() => setPeriod('month')}
              className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-colors ${
                period === 'month'
                  ? 'bg-emerald-500 text-white shadow-md'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              Ce Mois
            </button>
            
            <button
              onClick={() => setPeriod('year')}
              className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-colors ${
                period === 'year'
                  ? 'bg-emerald-500 text-white shadow-md'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              12 Mois
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 md:py-8">
        {/* Consommation en temps réel */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-8">
          <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
            <div className="p-3 sm:p-4">
              <p className="text-xs sm:text-sm font-semibold mb-1 sm:mb-2 opacity-90">Puissance</p>
              <p className="text-2xl sm:text-3xl md:text-4xl font-bold">{data.consumption.power.toFixed(1)}</p>
              <p className="text-xs sm:text-sm mt-1 opacity-75">Watts (W)</p>
            </div>
          </Card>

          <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
            <div className="p-3 sm:p-4">
              <p className="text-xs sm:text-sm font-semibold mb-1 sm:mb-2 opacity-90">Énergie</p>
              <p className="text-2xl sm:text-3xl md:text-4xl font-bold">{(data.costs.consumption.kwh).toFixed(3)}</p>
              <p className="text-xs sm:text-sm mt-1 opacity-75">kWh ce mois</p>
            </div>
          </Card>

          <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
            <div className="p-3 sm:p-4">
              <p className="text-xs sm:text-sm font-semibold mb-1 sm:mb-2 opacity-90">Courant</p>
              <p className="text-2xl sm:text-3xl md:text-4xl font-bold">{data.consumption.current.toFixed(2)}</p>
              <p className="text-xs sm:text-sm mt-1 opacity-75">Ampères (A)</p>
            </div>
          </Card>

          <Card className="bg-gradient-to-br from-orange-500 to-orange-600 text-white">
            <div className="p-3 sm:p-4">
              <p className="text-xs sm:text-sm font-semibold mb-1 sm:mb-2 opacity-90">Tension</p>
              <p className="text-2xl sm:text-3xl md:text-4xl font-bold">{data.consumption.voltage.toFixed(0)}</p>
              <p className="text-xs sm:text-sm mt-1 opacity-75">Volts (V)</p>
            </div>
          </Card>
        </div>

        {/* Estimation fin de mois */}
        {period === 'month' && estimation && (
          <Card className="mb-6 sm:mb-8 bg-gradient-to-br from-indigo-500 to-purple-600 text-white border-0">
            <div className="p-4 sm:p-6">
              <div className="flex items-center gap-3 mb-4 sm:mb-6">
                <span className="text-3xl sm:text-4xl">🔮</span>
                <div>
                  <h3 className="text-xl sm:text-2xl font-bold">Estimation Fin de Mois</h3>
                  <p className="text-indigo-100 text-xs sm:text-sm">
                    Basée sur votre consommation actuelle
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
                <div className="bg-white/20 backdrop-blur-sm rounded-xl p-3 sm:p-4">
                  <p className="text-xs opacity-75 mb-1">Actuelle</p>
                  <p className="text-lg sm:text-2xl font-bold">{estimation.currentConsumption.toFixed(1)} kWh</p>
                </div>
                <div className="bg-white/20 backdrop-blur-sm rounded-xl p-3 sm:p-4">
                  <p className="text-xs opacity-75 mb-1">Restante</p>
                  <p className="text-lg sm:text-2xl font-bold">{estimation.estimatedRemaining.toFixed(1)} kWh</p>
                </div>
                <div className="bg-white/20 backdrop-blur-sm rounded-xl p-3 sm:p-4">
                  <p className="text-xs opacity-75 mb-1">Total estimé</p>
                  <p className="text-lg sm:text-2xl font-bold">{estimation.estimatedTotal.toFixed(1)} kWh</p>
                </div>
                <div className="bg-white/20 backdrop-blur-sm rounded-xl p-3 sm:p-4">
                  <p className="text-xs opacity-75 mb-1">Coût estimé</p>
                  <p className="text-lg sm:text-2xl font-bold">{formatCurrency(estimation.estimatedCost)} F</p>
                </div>
              </div>

              <div className="mt-3 sm:mt-4 p-3 sm:p-4 bg-white/10 rounded-xl">
                <p className="text-xs sm:text-sm">
                  📊 Moyenne: <span className="font-bold">{estimation.avgPerDay} kWh/jour</span>
                  {' • '}
                  📅 Jours restants: <span className="font-bold">{estimation.daysRemaining}</span>
                </p>
              </div>
            </div>
          </Card>
        )}

        {/* Graphiques */}
        {history.length > 0 && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-6 sm:mb-8">
            {/* Graphique Consommation */}
            <Card title="📈 Historique Consommation (kWh)">
              <div className="p-3 sm:p-4">
                <ResponsiveContainer width="100%" height={250} className="sm:h-[300px]">
                  <LineChart data={history}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                    <XAxis 
                      dataKey="date" 
                      tick={{ fontSize: 11 }}
                      angle={-45}
                      textAnchor="end"
                      height={70}
                      interval={0}
                    />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend wrapperStyle={{ fontSize: '12px' }} />
                    <Line 
                      type="monotone" 
                      dataKey="kwh" 
                      name="Consommation (kWh)"
                      stroke="#10b981" 
                      strokeWidth={3}
                      dot={{ fill: '#10b981', r: 4 }}
                      activeDot={{ r: 6 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </Card>

            {/* Graphique Coûts */}
            <Card title="💰 Historique des Coûts (FCFA)">
              <div className="p-3 sm:p-4">
                <ResponsiveContainer width="100%" height={250} className="sm:h-[300px]">
                  <BarChart data={history}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                    <XAxis 
                      dataKey="date" 
                      tick={{ fontSize: 10 }}
                      angle={-45}
                      textAnchor="end"
                      height={60}
                    />
                    <YAxis tick={{ fontSize: 10 }} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend wrapperStyle={{ fontSize: '12px' }} />
                    <Bar 
                      dataKey="cost" 
                      name="Coût (FCFA)"
                      fill="#3b82f6" 
                      radius={[8, 8, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Card>
          </div>
        )}

        {/* Détail des coûts */}
        <Card title="💰 Détail des Coûts">
          <div className="p-4 sm:p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
              <div className="space-y-2 sm:space-y-3">
                <div className="flex justify-between items-center p-2 sm:p-3 bg-blue-50 rounded-lg">
                  <span className="text-xs sm:text-sm text-gray-700">Coût consommation (HT)</span>
                  <span className="text-sm sm:text-lg font-bold text-blue-600">
                    {formatCurrency(data.costs.consumption.baseCost)} F
                  </span>
                </div>

                <div className="flex justify-between items-center p-2 sm:p-3 bg-purple-50 rounded-lg">
                  <span className="text-xs sm:text-sm text-gray-700">TVA (18%)</span>
                  <span className="text-sm sm:text-lg font-bold text-purple-600">
                    {formatCurrency(data.costs.consumption.tvaConsommation)} F
                  </span>
                </div>

                <div className="flex justify-between items-center p-2 sm:p-3 bg-yellow-50 rounded-lg">
                  <span className="text-xs sm:text-sm text-gray-700">Prime fixe</span>
                  <span className="text-sm sm:text-lg font-bold text-yellow-600">
                    {formatCurrency(data.costs.fixedCharges.primeFixe)} F
                  </span>
                </div>
              </div>

              <div className="space-y-2 sm:space-y-3">
                <div className="flex justify-between items-center p-2 sm:p-3 bg-orange-50 rounded-lg">
                  <span className="text-xs sm:text-sm text-gray-700">Redevances totales</span>
                  <span className="text-sm sm:text-lg font-bold text-orange-600">
                    {formatCurrency(data.costs.taxes.totalRedevances)} F
                  </span>
                </div>

                <div className="flex justify-between items-center p-2 sm:p-3 bg-green-50 rounded-lg">
                  <span className="text-xs sm:text-sm text-gray-700">TVA Prime fixe</span>
                  <span className="text-sm sm:text-lg font-bold text-green-600">
                    {formatCurrency(data.costs.fixedCharges.tvaPrimeFixe)} F
                  </span>
                </div>

                <div className="flex justify-between items-center p-3 sm:p-4 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-lg">
                  <span className="text-sm sm:text-base font-bold">TOTAL À PAYER</span>
                  <span className="text-lg sm:text-2xl font-bold">
                    {formatCurrency(data.costs.totals.totalCost)} F
                  </span>
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* Information tarifaire */}
        <Card className="mt-4 sm:mt-6 bg-blue-50 border-2 border-blue-200">
          <div className="p-3 sm:p-4 flex items-start gap-3 sm:gap-4">
            <span className="text-2xl sm:text-3xl">ℹ️</span>
            <div>
              <h3 className="font-bold text-gray-800 mb-2 text-sm sm:text-base">Tarif CIE-CI 2024/2025</h3>
              <p className="text-xs sm:text-sm text-gray-700 leading-relaxed">
                Les coûts sont calculés selon le tarif officiel de la CIE incluant le coût de base (81.03 FCFA/kWh), 
                la TVA à 18%, la prime fixe mensuelle et les redevances réglementaires.
              </p>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default SimpleConsumption;